import { useEffect, useState } from "react";
import { MoreHorizontal, SlidersHorizontal, Pencil } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const MQ = "(max-width: 767px)";

export const useIsMobile = () => {
  const [m, setM] = useState(() => typeof window !== "undefined" && window.matchMedia(MQ).matches);
  useEffect(() => {
    const mq = window.matchMedia(MQ);
    const on = () => setM(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return m;
};

// Desktop: renders children inline. Phone: a trigger button + slide-up sheet holding the same children.
export const MobileCollapse = ({ isMobile, label, kind = "menu", testid, badge, children }) => {
  const [open, setOpen] = useState(false);
  if (!isMobile) return children;
  const Icon = kind === "filters" ? SlidersHorizontal : MoreHorizontal;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testid}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 active:bg-slate-100">
        <Icon className="w-4 h-4" /> {label}
        {badge ? <span className="ml-0.5 px-1.5 rounded-full bg-indigo-600 text-white text-[10px] font-bold">{badge}</span> : null}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-2xl px-4 pb-8" data-testid={`${testid}-sheet`}
          onClick={(e) => { if (e.target.closest("button[data-close-sheet]")) setOpen(false); }}>
          <SheetHeader><SheetTitle className="text-left">{label}</SheetTitle></SheetHeader>
          <div className="mt-3 flex flex-wrap items-center gap-2 [&>*]:shrink-0 [&_span.hidden]:!inline">{children}</div>
        </SheetContent>
      </Sheet>
    </>
  );
};

// Desktop: children when open. Phone: same children in a controlled slide-up sheet.
export const MobileSheetWrap = ({ isMobile, open, onOpenChange, title, testid, children }) => {
  if (!isMobile) return open ? children : null;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl px-4 pb-8" data-testid={testid}>
        <SheetHeader><SheetTitle className="text-left">{title}</SheetTitle></SheetHeader>
        <div className="mt-3 flex flex-wrap items-center gap-2 [&_span.hidden]:!inline">{children}</div>
      </SheetContent>
    </Sheet>
  );
};

// Compact phone card: title line, chips, then label/value pairs. Tap → onOpen.
export const MobileCard = ({ title, subtitle, chips, fields, tone = "", onOpen, testid, selected }) => (
  <button type="button" onClick={onOpen} data-testid={testid}
    className={`w-full text-left rounded-xl border px-3 py-2.5 bg-white active:bg-indigo-50 ${selected ? "border-red-400 bg-red-50" : "border-slate-200"} ${tone}`}>
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900 truncate">{title}</div>
        {subtitle && <div className="text-[11px] text-slate-500 truncate">{subtitle}</div>}
      </div>
      <div className="flex flex-wrap justify-end gap-1 shrink-0 max-w-[55%]">{chips}</div>
    </div>
    <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5">
      {fields.filter(Boolean).map(([k, v]) => (
        <div key={k} className="min-w-0 text-[11px]"><span className="text-slate-400">{k} </span><span className="font-medium text-slate-700 break-all">{v || "—"}</span></div>
      ))}
    </div>
    <div className="mt-1 flex justify-end text-[10px] text-indigo-600 font-medium"><Pencil className="w-3 h-3 mr-0.5" />Tap to edit</div>
  </button>
);
